// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid2,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
import { JobCostingItem } from '~/components/module/job';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { EMPTY_STRING } from '~/utils/constants/common';
import { formatCurrencyUSD, formatInputCurrency } from '~/utils/helper/common';
import { IFormJobCosting, IJobCostingDetail, IParamQueryJobDetail } from '~/utils/interface/jobs';
import { defaultValuesForm, schema } from './helper';
import { TypeJobDetailEnum } from '~/utils/enum/common';
import { additionalExpense, getDetailJob } from '~/thunks/job/jobThunk';
import { IBodyUpdate } from '~/utils/interface/common';
// Styles, images, icons
import styles from './JobCostingTab.module.scss';
import './JobCostingTab.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const JobCostingTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IFormJobCosting>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const {
    fields: additionalExpensesFields,
    append: appendAdditionalExpense,
    remove: removeAdditionalExpense,
  } = useFieldArray({
    control,
    name: 'additionalExpenses',
  });
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshJobDetail } = useAppSelector((state) => state.job);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobCostingDetail, setJobCostingDetail] = useState<IJobCostingDetail>();
  //#endregion Declare State

  //#region Declare Memo
  const dataTime = useMemo(() => {
    return [
      {
        title: t('job_detail_title_projected_time'),
        value: jobCostingDetail?.loadingTime,
        subTitle: t('job_detail_title_loading_time'),
      },
      {
        title: t('job_detail_title_delivery_time'),
        value: jobCostingDetail?.deliveryTime,
        subTitle: t('job_detail_title_hours_for_delivery'),
      },
      {
        title: t('job_detail_title_pickup_time'),
        value: jobCostingDetail?.pickupTime,
        subTitle: t('job_detail_title_hours_for_pickup'),
      },
      {
        title: t('job_detail_title_unloading_time'),
        value: jobCostingDetail?.unLoadingTime,
        subTitle: t('job_detail_title_hours_for_unloading'),
      },
      {
        title: t('job_detail_title_total_projected_time'),
        value: jobCostingDetail?.totalTime,
        subTitle: t('job_detail_title_hours_for_job'),
      },
    ];
  }, [jobCostingDetail]);

  const dataOverview = useMemo(() => {
    return [
      // {
      //   title: t('job_detail_title_job_revenue'),
      //   value: jobCostingDetail?.revenue?.totalAmount,
      //   subTitle: t('job_detail_title_total_revenue'),
      // },
      {
        title: t('job_detail_title_job_expenses'),
        value: jobCostingDetail?.expenses?.totalAmount,
        subTitle: t('job_detail_title_total_expenses'),
      },
      // {
      //   title: t('job_detail_title_profit_margin'),
      //   value: jobCostingDetail?.profitMargin ? `${jobCostingDetail?.profitMargin}%` : EMPTY_STRING,
      //   subTitle: t('job_detail_title_profit_margin_percentage'),
      // },
    ];
  }, [jobCostingDetail]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!jobCostingDetail) return;
    const newData: IFormJobCosting = {
      additionalExpenses: jobCostingDetail?.expenses?.additionalExpenses.map((item) => ({
        ...item,
        idDetail: item.id,
      })),
    };

    reset(newData);
  }, [jobCostingDetail]);

  useEffect(() => {
    if (!jobId) return;

    const params: IParamQueryJobDetail = {
      jobId: jobId,
      type: TypeJobDetailEnum.COSTING,
    };

    handleGetDetailJob(params);
  }, [jobId]);

  useEffect(() => {
    if (!isRefreshJobDetail || !jobId) return;

    const params: IParamQueryJobDetail = {
      jobId: jobId,
      type: TypeJobDetailEnum.COSTING,
    };

    handleGetDetailJob(params);
  }, [isRefreshJobDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSave = (data: IFormJobCosting) => {
    if (!jobId) return;

    setIsLoading(true);

    const newData = {
      ...data,
      additionalExpenses: data?.additionalExpenses?.map(({ id, idDetail, ...item }) => ({
        ...item,
        id: idDetail,
      })),
    };

    const payload: IBodyUpdate<IFormJobCosting> = {
      id: jobId,
      body: newData,
    };

    dispatch(additionalExpense(payload))
      .unwrap()
      .then(() => {})
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleAddAdditionalExpense = () => {
    appendAdditionalExpense({
      description: EMPTY_STRING,
      amount: EMPTY_STRING,
    });
  };

  const handleRemoveAdditionalExpense = (index: number, id?: string) => {
    if (id) {
      setValue('deleteAdditionalExpenseIds', [...(getValues('deleteAdditionalExpenseIds') || []), id]);
    }

    removeAdditionalExpense(index);
  };

  const handleGetDetailJob = (params: IParamQueryJobDetail) => {
    setIsLoading(true);

    dispatch(getDetailJob(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setJobCostingDetail(res.data as IJobCostingDetail);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function
  return (
    <Box id='infoJobTabComponent' component={'form'} onSubmit={handleSubmit(handleSave)} className={cx('container')}>
      <Stack direction='column' gap={5} className={cx('viewBoxTime')}>
        <Stack className={cx('viewHeader')}>
          <Typography className={cx('title')}>{t('job_detail_title_costing')}</Typography>
        </Stack>

        <Stack direction='row' alignItems='center' gap={6} className={cx('viewStatistic')}>
          {dataTime && dataTime.map((item) => <JobCostingItem data={item} />)}
        </Stack>

        <Stack direction='column' alignItems='start' gap={2} className={cx('alert')}>
          <Stack direction='row' gap={2}>
            <img
              src={icons.commonIconWarningError}
              alt={t('common_alt_img', { ns: 'common/base' })}
              width={20}
              height={20}
            />
            <Typography className={cx('alertTitle')}>{t('job_detail_costing_alert_title')}</Typography>
          </Stack>
          <Typography className={cx('alertSubTitle')}>{t('task_table_action_alert_subtitle')}</Typography>
        </Stack>

        <Stack className={cx('viewHeader')}>
          <Typography className={cx('title')}>{t('job_detail_title_costing_overview')}</Typography>
        </Stack>

        <Stack direction='row' alignItems='center' gap={6} justifyContent={'space-between'} width={'33%'}>
          {dataOverview && dataOverview.map((item) => <JobCostingItem data={item} />)}
        </Stack>

        <Stack direction={'column'} className={cx('viewCostBreakdown')}>
          <Stack p={4}>
            <Typography className={cx('title')}>{t('job_detail_title_cost_breakdown')}</Typography>
          </Stack>

          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            className={cx('viewHeaderCost')}
          >
            <Typography className={cx('titleCost')}>{t('job_detail_title_category')}</Typography>
            <Typography className={cx('titleCost')}>{t('job_detail_title_amount')}</Typography>
          </Stack>

          <Stack>
            {/* <Accordion defaultExpanded>
              <AccordionSummary
                className={cx('summaryButton')}
                expandIcon={<img src={icons.commonIconArrowBottom} alt={t('common_alt_img', { ns: 'common/base' })} />}
              >
                <Typography className={cx('subtitleCost')}>{t('job_detail_title_job_revenue')}</Typography>
                <Typography className={cx('subValueCost')}>
                  {formatCurrencyUSD({ value: jobCostingDetail?.revenue?.totalAmount })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={cx('detailsCost')}>
                <Stack spacing={3} className={cx('detailsCostList')}>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_rental_item_total')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.revenue?.totalItems })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_delivery_pickup_total')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.revenue?.totalDelivery })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_damage_waiver')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.revenue?.damageWaiver })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_labor_fees')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.revenue?.laborFee })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_tax')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.revenue?.tax })}
                    </Typography>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion> */}

            <Accordion defaultExpanded>
              <AccordionSummary
                className={cx('summaryButton')}
                expandIcon={<img src={icons.commonIconArrowBottom} alt={t('common_alt_img', { ns: 'common/base' })} />}
              >
                <Typography className={cx('subtitleCost')}>{t('job_detail_title_job_expenses')}</Typography>
                <Typography className={cx('subValueCostExp')}>
                  {formatCurrencyUSD({ value: jobCostingDetail?.expenses?.totalAmount, showDecimal: true })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={cx('detailsCost')}>
                <Stack spacing={3} className={cx('detailsCostList')}>
                  <Typography className={cx('subLabelCost')}>{t('job_detail_title_direct_labor_inputs')}</Typography>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_loading')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.expenses?.loadingAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_delivery')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.expenses?.deliveryAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_pickup')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.expenses?.pickupAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('job_detail_title_unloading')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: jobCostingDetail?.expenses?.unloadingAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack className={cx('detailsDirectCost')}>
                  <Typography className={cx('subLabelCost')}>{t('job_detail_title_direct_labor_subtotal')}</Typography>
                  <Typography mt={2} className={cx('subLabelCost')}>
                    {t('job_detail_title_other_expenses')}
                  </Typography>
                  <Stack spacing={3}>
                    <Stack direction='row' justifyContent='space-between'>
                      <Typography className={cx('labelCost')}>{t('job_detail_title_additional_labor')}</Typography>
                      <Typography className={cx('valueCost')}>
                        {formatCurrencyUSD({
                          value: jobCostingDetail?.expenses?.additionalLaborAmount,
                          showDecimal: true,
                        })}
                      </Typography>
                    </Stack>
                    <Stack direction='row' justifyContent='space-between'>
                      <Typography className={cx('labelCost')}>{t('job_detail_title_mileage')}</Typography>
                      <Typography className={cx('valueCost')}>
                        {formatCurrencyUSD({
                          value: Number(jobCostingDetail?.expenses?.mileageAmount),
                          showDecimal: true,
                        })}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography mt={2} className={cx('subLabelCost')}>
                    {t('job_detail_title_additional_expenses')}
                  </Typography>

                  <Stack direction={'column'} spacing={3} width={'100%'}>
                    {additionalExpensesFields.map((field, index) => (
                      <Grid2 key={index} spacing={4} container>
                        <Grid2 size={9}>
                          <Controller
                            key={`${field.id}-${index}-description`}
                            control={control}
                            name={`additionalExpenses.${index}.description`}
                            render={({ field }) => (
                              <BaseInput
                                placeholder={t('job_detail_title_expense_description')}
                                value={field.value}
                                onChange={field.onChange}
                                errorMessage={errors?.additionalExpenses?.[index]?.description?.message}
                              />
                            )}
                          />
                        </Grid2>

                        <Grid2 className={cx('viewPrice')} size={2.5}>
                          <Typography className={cx('labelCost')} mt={'14px'}>
                            $
                          </Typography>

                          <Stack direction={'column'} width={'100%'}>
                            <Controller
                              key={`${field.id}-${index}-amount`}
                              control={control}
                              name={`additionalExpenses.${index}.amount`}
                              render={({ field }) => (
                                <BaseInput
                                  placeholder={t('common_text_placeholder_currency', {
                                    ns: 'common/base',
                                  })}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(formatInputCurrency(event.target.value));
                                  }}
                                  errorMessage={errors?.additionalExpenses?.[index]?.amount?.message}
                                />
                              )}
                            />
                          </Stack>
                        </Grid2>

                        <Grid2 size={0.5} className={cx('viewRemove')}>
                          <IconButton
                            key={`${field.id}-${index}-remove`}
                            className={cx('buttonRemove')}
                            onClick={() => handleRemoveAdditionalExpense(index, field.idDetail)}
                          >
                            <img src={icons.commonIconTrash} alt={t('common_alt_img')} />
                          </IconButton>
                        </Grid2>
                      </Grid2>
                    ))}
                  </Stack>

                  <Typography component={'span'} className={cx('buttonAdd')} onClick={handleAddAdditionalExpense}>
                    {t('job_detail_title_add_additional_expense')}
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
          {/* <Stack className={cx('viewTotalCost')}>
            <Typography className={cx('totalCostTitle')}>{t('job_detail_title_total_profit')}</Typography>
            <Typography className={cx('totalCostValue')}>
              {formatCurrencyUSD({ value: jobCostingDetail?.totalProfit })}
            </Typography>
          </Stack> */}
        </Stack>
      </Stack>

      <Button type='submit' variant='contained' className={cx('buttonAction')}>
        {t('common_action_save', { ns: 'common/base' })}
      </Button>

      {isLoading && <Loading />}
    </Box>
  );
};

export default JobCostingTab;
